import React from 'react';
import { Text } from '@shopify/polaris';
import RefreshTargetDetailLines from './RefreshTargetDetailLines';
import RefreshTargetStatusPanel from './RefreshTargetStatusPanel';
import { REFRESH_TARGET_LABELS } from '../data/refreshTargetDetails';
import { MODULE_COLORS } from '../utils/moduleConfig';

function RefreshTargetOption({
  targetKey,
  darkMode,
  checked,
  onChange,
  statusContext
}) {
  return (
    <label
      className="RefreshTargetOption"
      style={{ backgroundColor: darkMode ? '#252525' : '#fafbfb' }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="RefreshTargetOption__checkbox"
      />
      <div className="RefreshTargetOption__body">
        <div className="RefreshTargetOption__row">
          <div className="RefreshTargetOption__main">
            <Text variant="bodyMd" fontWeight="semibold">{REFRESH_TARGET_LABELS[targetKey]}</Text>
            <RefreshTargetDetailLines targetKey={targetKey} darkMode={darkMode} />
          </div>
          <div className="RefreshTargetOption__status">
            <RefreshTargetStatusPanel targetKey={targetKey} context={statusContext} />
          </div>
        </div>
      </div>
    </label>
  );
}
function RefreshModuleGroup({
  group,
  darkMode,
  selectedStores,
  setSelectedStores,
  hasModulePermission,
  statusContext
}) {
  if (group.permission && !hasModulePermission(group.permission)) {
    return null;
  }

  const moduleColors = MODULE_COLORS[group.moduleKey] || MODULE_COLORS.warehouse;
  const accent = moduleColors.indicator || moduleColors.buttonActive || moduleColors.base;

  return (
    <section
      className="RefreshModuleGroup"
      style={{
        borderColor: darkMode ? '#3d3d3d' : '#dfe3e8',
        borderLeftColor: accent
      }}
    >
      <header className="RefreshModuleGroup__header">
        <div className="RefreshModuleGroup__titleRow">
          <span
            className="RefreshModuleGroup__badge"
            style={{
              backgroundColor: darkMode ? `${accent}33` : moduleColors.accentBadge,
              color: darkMode ? '#e5e5e5' : moduleColors.header
            }}
          >
            {moduleColors.badgeLabel || group.title.toUpperCase()}
          </span>
          <Text variant="headingSm" as="h3">{group.title}</Text>
        </div>
        <Text variant="bodySm" tone="subdued">{group.description}</Text>
      </header>

      <div className="RefreshModuleGroup__sections">
        {group.sections.map((section) => (
          <div key={section.label || section.targetKeys.join('-')} className="RefreshModuleGroup__section">
            {section.label && (
              <div className="RefreshModuleGroup__sectionHead">
                <Text variant="bodyMd" fontWeight="semibold" as="h4">{section.label}</Text>
                {section.hint && (
                  <Text variant="bodySm" tone="subdued">{section.hint}</Text>
                )}
              </div>
            )}
            <div className="RefreshModuleGroup__targets">
              {section.targetKeys.map((targetKey) => (
                <RefreshTargetOption
                  key={targetKey}
                  targetKey={targetKey}
                  darkMode={darkMode}
                  checked={!!selectedStores[targetKey]}
                  onChange={(e) => setSelectedStores({
                    ...selectedStores,
                    [targetKey]: e.target.checked
                  })}
                  statusContext={statusContext}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function RefreshDatabasePicker({
  darkMode,
  refreshRun,
  selectedStores,
  setSelectedStores,
  hasModulePermission,
  cacheStatus,
  refreshTargetStatuses,
  isRefreshTargetInCurrentRun,
  plannerSyncJobs,
  plannerCostBackfillStatus,
  plannerSupplierSkuSyncStatus,
  activeRunStatusRows,
  getTargetRunStatusTone,
  getTargetRunStatusLabel,
  moduleGroups
}) {
  const statusContext = {
    cacheStatus,
    refreshTargetStatuses,
    isRefreshTargetInCurrentRun,
    plannerSyncJobs,
    plannerCostBackfillStatus,
    plannerSupplierSkuSyncStatus
  };

  return (
    <div className="RefreshDatabasePicker">
      {refreshRun && (
        <div className="RefreshDatabasePicker__banner">
          <Text variant="bodySm" tone="subdued">
            Refresh in progress — close anytime and reopen from the DB button to check status.
          </Text>
        </div>
      )}

      <Text variant="bodyMd" tone="subdued">
        Targets are grouped by app module. Each runs independently — read the steps under a checkbox before selecting it.
      </Text>

      {moduleGroups.map((group) => (
        <RefreshModuleGroup
          key={group.id}
          group={group}
          darkMode={darkMode}
          selectedStores={selectedStores}
          setSelectedStores={setSelectedStores}
          hasModulePermission={hasModulePermission}
          statusContext={statusContext}
        />
      ))}

      {activeRunStatusRows.length > 0 && (
        <div
          className="RefreshDatabasePicker__progress"
          style={{ borderColor: darkMode ? '#3d3d3d' : '#dfe3e8' }}
        >
          <Text variant="headingSm" as="h3">{refreshRun ? 'Refresh progress' : 'Selected target status'}</Text>
          <div className="RefreshDatabasePicker__progressList">
            {activeRunStatusRows.map((row) => (
              <div key={row.key} className="RefreshDatabasePicker__progressRow">
                <div className="RefreshDatabasePicker__progressMain">
                  <Text variant="bodySm" fontWeight="semibold">{row.label}</Text>
                  {row.description && (
                    <Text variant="bodySm" tone="subdued">{row.description}</Text>
                  )}
                  {row.status === 'failed' && row.error && (
                    <Text variant="bodySm" tone="critical">{row.error}</Text>
                  )}
                </div>
                <Text variant="bodySm" tone={getTargetRunStatusTone(row.status)}>
                  {getTargetRunStatusLabel(row.status)}
                </Text>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
